import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {debounceTime, map, mergeMap, of, switchMap} from 'rxjs';
import {catchError, exhaustMap} from 'rxjs/operators';
import {AuthService} from '@aviary/services/auth.service';
import {openConfirmationDialog} from '@aviary/store/common/effect-dialog.helpers';
import {isPopulatedString} from '@aviary/store/common/typing.helpers';
import {RouterActions, RouterSelectors, SnackBarActions} from '@store/aviary/aviary.adapter';
import {noAuthWhenRouterHasNavigatedTo, skipUnless, withLatestFromSelectors} from '@store/common/effects.helpers';
import {PatronActions} from '@store/patron/patron.actions';
import {PatronService} from '@store/patron/patron.service';
import {RegistrationActions} from '@store/registration/registration.actions';
import {RegistrationSelectors} from '@store/registration/registration.selectors';
import {RegistrationService} from '@store/registration/registration.service';
import {PatronSelectors} from '@store/patron/patron.selectors';
import {CreateAccountDialog} from '@app/registration/create-account.dialog';

@Injectable({providedIn: 'root'})
export class RegistrationEffects {

  private readonly actions: Actions = inject(Actions);
  private readonly authService: AuthService = inject(AuthService);
  private readonly service: RegistrationService = inject(RegistrationService);
  private readonly patronService: PatronService = inject(PatronService);

  public showCreateAccountDialog = createEffect(() =>
    this.actions.pipe(
      ofType(RegistrationActions.showCreateAccount),
      openConfirmationDialog(() => ({
        componentClass: CreateAccountDialog,
        data: undefined,
        onConfirm: () => ([])
      }))
    )
  );

  public validateUniquePatronEmail = createEffect(() =>
    this.actions.pipe(
      ofType(RegistrationActions.validatePatronEmailAvailable),
      debounceTime(500),
      switchMap(({email}) => this.service.validatePatronEmailAvailable(email).pipe(
        map(response => RegistrationActions.validatePatronEmailAvailableSuccess(response)),
        catchError((error: unknown) => of(RegistrationActions.validatePatronEmailAvailableFailure(error)))
      ))
    )
  );

  public handleEmailLanding = createEffect(() =>
    this.actions.pipe(
      noAuthWhenRouterHasNavigatedTo('REGISTER_VERIFY_EMAIL_LANDING'),
      withLatestFromSelectors(RouterSelectors.fragment),
      mergeMap(([, fragment]) => {
        const urlParams = new URLSearchParams(fragment ?? '');
        const uuid = urlParams.get('uuid');
        const code = urlParams.get('code');
        if (isPopulatedString(uuid) && isPopulatedString(code)) {
          return [RegistrationActions.verifyEmail(code, uuid)]
        } else {
          return [
            SnackBarActions.showError('Missing email validation information.'),
            RouterActions.navigateTo('INDEX')
          ];
        }
      })
    )
  );

  public skipLookupAccountIfLoggedIn = createEffect(() =>
    this.actions.pipe(
      noAuthWhenRouterHasNavigatedTo('REGISTER_EXISTING_CARD'),
      skipUnless(PatronSelectors.Auth.isEligibleForRegisterExistingAccount),
      map(() => RegistrationActions.completeRegistrationStep('lookupAccount'))
    )
  );

  public lookupAccount = createEffect(() =>
    this.actions.pipe(
      ofType(RegistrationActions.lookupAccount),
      switchMap(({cardNumber, PIN}) => this.patronService.lookupAccount(cardNumber, PIN).pipe(
        switchMap(({patron, loginToken}) => this.authService.loginWithToken(loginToken).pipe(
          mergeMap(() => [
            PatronActions.loginPatronSuccess(),
            RegistrationActions.lookupAccountSuccess(cardNumber, PIN, patron),
            RegistrationActions.completeRegistrationStep('lookupAccount')
          ]),
          catchError((error: unknown) => of(PatronActions.loginPatronFailure(error)))
        )),
        catchError((error: unknown) => of(RegistrationActions.lookupAccountFailure(error)))
      ))
    )
  );

  public requestVerificationEmail = createEffect(() =>
    this.actions.pipe(
      ofType(RegistrationActions.requestVerificationEmail),
      withLatestFromSelectors(RegistrationSelectors.Registration.email),
      map(([{email}, storeEmail]) => email ?? storeEmail),
      switchMap((email) => this.service.requestVerificationEmail(email).pipe(
        mergeMap((verificationUUID) => [
          RegistrationActions.requestVerificationEmailSuccess(email, verificationUUID),
          RegistrationActions.setVerificationExpired(false),
          RegistrationActions.completeRegistrationStep('enterEmail'),
          SnackBarActions.showSuccess(`A verification code was sent to: ${email}`)
        ]),
        catchError((error: unknown) => of(RegistrationActions.requestVerificationEmailFailure(error)))
      ))
    )
  );

  public checkVerificationStatus = createEffect(() =>
    this.actions.pipe(
      ofType(RegistrationActions.checkVerificationStatus),
      switchMap(({verificationUUID}) => this.service.checkVerificationStatus(verificationUUID).pipe(
        mergeMap((verificationStatusView) => {
          switch (verificationStatusView.status) {
            case 'APPROVED':
              // verified on another device, skip to create password step
              return [
                RegistrationActions.completeRegistrationStep('enterEmail'),
                RegistrationActions.completeRegistrationStep('verifyEmail'),
                RegistrationActions.checkVerificationStatusSuccess()
              ];
            case 'ACCEPTED':
              // registration has been completed on another device
              return [
                SnackBarActions.showSuccess('Registration has been completed.'),
                RegistrationActions.checkVerificationStatusSuccess(),
                RouterActions.navigateTo('DASHBOARD')
              ];
            // navigating to DASHBOARD or INDEX is functionally identical, since they check login state and may redirect to the other
            // the navigate actions above and below are just guessing at which one is more likely appropriate
            case 'CANCELED':
              // registration was canceled
              return [
                SnackBarActions.showSuccess('Registration has been canceled. Please refresh and try again.'),
                RegistrationActions.checkVerificationStatusSuccess(),
                RouterActions.navigateTo('INDEX')
              ];
            case 'EXPIRED':
              return [
                RegistrationActions.setVerificationExpired(true),
                RegistrationActions.checkVerificationStatusSuccess()
              ];
          }
          return [RegistrationActions.checkVerificationStatusSuccess()];
        }),
        catchError((error: unknown) => of(RegistrationActions.checkVerificationStatusFailure(error)))
      ))
    )
  );

  public verifyEmail = createEffect(() =>
    this.actions.pipe(
      ofType(RegistrationActions.verifyEmail),
      withLatestFromSelectors(RegistrationSelectors.Registration.verificationUUID),
      // coming from the email link landing page, attachedVerificationUUID is included and navigation to the correct page is required
      // coming from the verification code form submit, there is no attachedVerificationUUID but there's also no need to navigate
      map(([{code, attachedVerificationUUID}, storedVerificationUUID]) =>
        isPopulatedString(attachedVerificationUUID) ?
          {code, uuid: attachedVerificationUUID, doNavigates: true} : {code, uuid: storedVerificationUUID, doNavigates: false}),
      switchMap(({code, uuid, doNavigates}) =>
        this.service.verifyEmail(code, uuid, doNavigates).pipe(
          mergeMap((verificationStatus) => {
            return [
              ...doNavigates && verificationStatus.isExistingPatron ? [
                RouterActions.navigateTo('REGISTER_EXISTING_CARD'),
              ] : [],
              ...doNavigates && !verificationStatus.isExistingPatron ? [
                RouterActions.navigateTo('REGISTER'),
                RegistrationActions.completeRegistrationStep('enterEmail')
              ] : [],
              RegistrationActions.requestVerificationEmailSuccess(verificationStatus.email, uuid),
              RegistrationActions.verifyEmailSuccess(),
              RegistrationActions.completeRegistrationStep('verifyEmail')
            ];
          }),
          catchError((error: unknown) => of(RegistrationActions.verifyEmailFailure(error)))
        ))
    )
  );

  public registerLoginPatron = createEffect(() =>
    this.actions.pipe(
      ofType(RegistrationActions.registerLoginPatron),
      switchMap(({email, password}) => this.authService.login(email, password).pipe(
        map(() => PatronActions.loginPatronSuccess()),
        catchError((error: unknown) => of(PatronActions.loginPatronFailure(error)))
      ))
    )
  );

  public register = createEffect(() =>
    this.actions.pipe(
      ofType(RegistrationActions.register),
      withLatestFromSelectors(RegistrationSelectors.Registration.email, RegistrationSelectors.Registration.value),
      exhaustMap(([, email, value]) => this.service.register(value).pipe(
        mergeMap(() => [
          RegistrationActions.registerSuccess(),
          RegistrationActions.completeRegistrationStep('createPassword'),
          RegistrationActions.registerLoginPatron(email, value.password)
        ]),
        catchError((error: unknown) => of(RegistrationActions.registerFailure(error)))
      ))
    )
  );

  public registerExisting = createEffect(() =>
    this.actions.pipe(
      ofType(RegistrationActions.registerExisting),
      withLatestFromSelectors(RegistrationSelectors.Registration.verificationUUID, RegistrationSelectors.Registration.value, RegistrationSelectors.Registration.email),
      exhaustMap(([, verificationUUID, value, email]) => this.service.registerExisting(value, verificationUUID).pipe(
        mergeMap(() => [
          RegistrationActions.registerExistingSuccess(),
          RegistrationActions.completeRegistrationStep('createPassword'),
          RegistrationActions.registerLoginPatron(email, value.password)
        ]),
        catchError((error: unknown) => of(RegistrationActions.registerExistingFailure(error)))
      ))
    )
  );
}
